import React, { useEffect, useRef, useState } from 'react';

// The celebration over the chat when they've signed up — like iMessage's
// effects — themed to the community: theatre groups get the curtains opening
// with spotlights panning, biker groups two motorbikes zooming past, the rest
// a burst of confetti. Each plays once (3–4 seconds), never blocks a tap, and
// is skipped for people who've asked for reduced motion.
const COLOURS = ['#a3e635', '#fde047', '#6366f1', '#ec4899', '#38bdf8', '#f97316', '#ffffff'];
const DURATION_MS = 3200;

const Confetti = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Two bursts from the bottom corners, arcing up and over.
    const pieces = Array.from({ length: 170 }, (_, i) => {
      const fromLeft = i % 2 === 0;
      const angle = (fromLeft ? -60 : -120) + (Math.random() * 40 - 20);
      const speed = 11 + Math.random() * 9;
      return {
        x: fromLeft ? w * 0.08 : w * 0.92,
        y: h * 0.95,
        vx: Math.cos((angle * Math.PI) / 180) * speed,
        vy: Math.sin((angle * Math.PI) / 180) * speed,
        size: 6 + Math.random() * 6,
        colour: COLOURS[i % COLOURS.length],
        spin: Math.random() * Math.PI,
        spinSpeed: (Math.random() - 0.5) * 0.35,
        round: Math.random() < 0.3,
      };
    });
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = now - start;
      ctx.clearRect(0, 0, w, h);
      ctx.globalAlpha = t > DURATION_MS - 700 ? Math.max(0, (DURATION_MS - t) / 700) : 1;
      pieces.forEach((p) => {
        p.vy += 0.32; // gravity
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.spin += p.spinSpeed;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.spin);
        ctx.fillStyle = p.colour;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        }
        ctx.restore();
      });
      if (t < DURATION_MS) frame = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, w, h);
    };
    frame = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="aio-fanfare" aria-hidden="true" />;
};

// Which celebration suits a community, from its name (and tags).
export const fanfareTheme = (communityTitle = '') => {
  const t = String(communityTitle).toLowerCase();
  if (/theatre|theater|musical|west end|\bshows?\b|opera|ballet/.test(t)) return 'theatre';
  if (/bike|biker|motorcycl|motorbike|riders?\b/.test(t)) return 'bikers';
  return 'confetti';
};

const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Shown for `ms`, then removed.
const useShownFor = (ms) => {
  const [shown, setShown] = useState(() => !reducedMotion());
  useEffect(() => {
    if (!shown) return undefined;
    const id = setTimeout(() => setShown(false), ms);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return shown;
};

// Theatre: velvet curtains with a gold-fringed pelmet sweep open, while two
// spotlights pan across the stage.
const CurtainCall = () => {
  const shown = useShownFor(4000);
  if (!shown) return null;
  return (
    <div className="aio-stage" aria-hidden="true">
      <div className="aio-spot aio-spot--left" />
      <div className="aio-spot aio-spot--right" />
      <div className="aio-curtain aio-curtain--left" />
      <div className="aio-curtain aio-curtain--right" />
      <div className="aio-pelmet" />
    </div>
  );
};

// A side-on motorbike with its rider, facing right.
const Motorbike = ({ colour }) => (
  <svg viewBox="0 0 140 80" width="100%" height="100%">
    <g stroke="#0f172a" strokeWidth="5" fill="none">
      <circle cx="28" cy="58" r="17" />
      <circle cx="110" cy="58" r="17" />
    </g>
    <circle cx="28" cy="58" r="5" fill="#94a3b8" />
    <circle cx="110" cy="58" r="5" fill="#94a3b8" />
    {/* frame, tank and seat */}
    <path d="M28 58 L52 38 L84 38 L110 58" stroke="#0f172a" strokeWidth="5" fill="none" strokeLinejoin="round" />
    <path d="M46 40 Q60 24 82 28 L98 34 L92 44 L50 46 Z" fill={colour} />
    <path d="M96 36 L110 30 L114 36 L100 42 Z" fill={colour} />
    <rect x="38" y="44" width="40" height="8" rx="4" fill="#334155" />
    {/* rider */}
    <path d="M62 28 Q70 10 84 14 L88 22 L74 30 Z" fill="#1e293b" />
    <circle cx="88" cy="10" r="8" fill="#f8fafc" stroke="#0f172a" strokeWidth="2.5" />
    <path d="M86 8 L96 8 L96 13 L88 13 Z" fill="#0f172a" />
  </svg>
);

// Bikers: two motorbikes zoom across, one after the other, with speed lines.
const RideBy = () => {
  const shown = useShownFor(3200);
  if (!shown) return null;
  return (
    <div className="aio-ride" aria-hidden="true">
      <div className="aio-bike aio-bike--one"><div className="aio-speedlines" /><Motorbike colour="#84cc16" /></div>
      <div className="aio-bike aio-bike--two"><div className="aio-speedlines" /><Motorbike colour="#6366f1" /></div>
    </div>
  );
};

const Fanfare = ({ theme = 'confetti' }) => {
  if (theme === 'theatre') return <CurtainCall />;
  if (theme === 'bikers') return <RideBy />;
  return <Confetti />;
};

export default Fanfare;
